/**
 * Las repeticiones de una campaña (28/10/2026): lo que comparten la página «Repetir esta campaña», el detalle (la tarjeta
 * de la madre y el renglón de la hija), la lista (el chip «Se repite») y el asistente de una que quedó en borrador. Las fechas y las reglas son del backend
 * (`util/repeticion.util`: la zona, el fin de mes, cuándo sale sola): acá sólo los textos, las rutas y la regla mientras
 * se edita.
 */

import type { CadaRepeticion, CampanaDetalle, DecisionRepeticion, EstadoCampanaOficial, EstadoRepeticion, ReglaRepeticion } from "../types";
import { diaYMes } from "./asistente";
import { paisDeZona } from "./zonas";

const B = "/activa/oficial/campanas";

/** La página para elegir cada cuánto (una página, no una ventana encima). */
export const rutaRepetir = (id: string) => `${B}/${encodeURIComponent(id)}/repetir`;

export const CADAS: { valor: CadaRepeticion; titulo: string }[] = [
  { valor: "semana", titulo: "Cada semana" },
  { valor: "2semanas", titulo: "Cada 2 semanas" },
  { valor: "mes", titulo: "Cada mes" },
];

/** Los días como los cuenta el backend (0 = domingo … 6 = sábado), en el orden de la semana de acá: de lunes a domingo. */
export const DIAS_SEMANA: { valor: number; titulo: string }[] = [
  { valor: 1, titulo: "Lunes" },
  { valor: 2, titulo: "Martes" },
  { valor: 3, titulo: "Miércoles" },
  { valor: 4, titulo: "Jueves" },
  { valor: 5, titulo: "Viernes" },
  { valor: 6, titulo: "Sábado" },
  { valor: 0, titulo: "Domingo" },
];

export const VECES_MAX = 52;
/** Las que manda el backend en «Las próximas» (`PROXIMAS_A_MOSTRAR` de `util/repeticion.util`). */
export const PROXIMAS_A_MOSTRAR = 6;

export const ESTADO_REPETICION: Record<EstadoRepeticion, string> = {
  activa: "Se repite",
  pausada: "Repetición pausada",
  terminada: "Ya no se repite",
};

/** La regla mientras se edita: los números como texto (un campo vacío no es un 0) y cuál de los dos finales se eligió. */
export interface ReglaEnEdicion {
  cada: CadaRepeticion;
  diaSemana: number;
  diaMes: string;
  hora: string;
  zona: string;
  fin: "veces" | "hasta";
  veces: string;
  hasta: string;
  saleSola: boolean;
}

export function reglaEnEdicion(r: ReglaRepeticion): ReglaEnEdicion {
  const mes = r.cada === "mes";
  return {
    cada: r.cada,
    diaSemana: mes ? 1 : r.dia,
    diaMes: mes ? String(r.dia) : "1",
    hora: r.hora,
    zona: r.zona,
    fin: r.hasta ? "hasta" : "veces",
    veces: r.veces ? String(r.veces) : "4",
    hasta: r.hasta || "",
    saleSola: r.saleSola,
  };
}

/** La regla para el backend; null mientras falta algo que se ve a simple vista (el resto lo valida el backend). */
export function reglaParaEnviar(e: ReglaEnEdicion): ReglaRepeticion | null {
  // un campo vacío no es un 0 (`Number("")` lo sería)
  const entero = (t: string) => (/^\d+$/.test(String(t).trim()) ? Number(t) : NaN);
  const dia = e.cada === "mes" ? entero(e.diaMes) : e.diaSemana;
  if (!Number.isInteger(dia) || !/^\d{2}:\d{2}$/.test(e.hora)) return null;
  const veces = entero(e.veces);
  if (e.fin === "veces" && !Number.isInteger(veces)) return null;
  if (e.fin === "hasta" && !/^\d{4}-\d{2}-\d{2}$/.test(e.hasta)) return null;
  return { cada: e.cada, dia, hora: e.hora, zona: e.zona, hasta: e.fin === "hasta" ? e.hasta : null, veces: e.fin === "veces" ? veces : null, saleSola: e.saleSola };
}

const DIAS_CORTOS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const formatos = new Map<string, Intl.DateTimeFormat>();

/**
 * «mié 12/11 19:00»: una fecha de la serie en la hora de la zona de la repetición (no en la del navegador). El día de la
 * semana se calcula de la fecha y no se le pide a Intl: su abreviatura cambia de una versión a otra («mié.», «mié»).
 */
export function fechaDeLaSerie(iso: string | Date | null | undefined, zona: string): string {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "—";
  let f = formatos.get(zona);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: zona, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    formatos.set(zona, f);
  }
  const p = Object.fromEntries(f.formatToParts(t).filter((x) => x.type !== "literal").map((x) => [x.type, x.value]));
  const dia = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day))).getUTCDay();
  return `${DIAS_CORTOS[dia]} ${p.day}/${p.month} ${String(Number(p.hour) % 24).padStart(2, "0")}:${p.minute}`;
}

/** Cómo quedó una repetición, en palabras: lo que dice la lista de las repeticiones en el detalle de la madre. */
export function textoDecision(decision: DecisionRepeticion | null, estado: EstadoCampanaOficial): string {
  // cancelada: lo dice su chip (una programada que se canceló con la original nunca «salió sola»)
  if (estado === "cancelada") return "";
  if (estado === "borrador") return decision === "decidiendo" || decision === null ? "Armándose" : "Quedó en borrador";
  if (decision === "salio") return "Salió sola";
  return "Confirmada a mano";
}

/* ── Una repetición que quedó en borrador, en su asistente (29/10/2026) ── */

/** «Repetición 3 de «Campeones de octubre»»: el renglón del panel «Tu campaña». */
export const textoRepeticionDe = (de: { n: number; nombre: string }) => `Repetición ${de.n} de «${de.nombre}»`;

/**
 * La fecha que le toca a una repetición, si todavía se puede programar (después de los 5 minutos del selector): «Programar»
 * viene con ésa. Es una sugerencia a la vista, como la franja: «Ahora» o «Programar» se sigue eligiendo a propósito.
 * null si ya pasó (o no vino).
 */
export function fechaQueLeToca(programadaPara: string | null | undefined, ahora: Date): string | null {
  const t = programadaPara ? new Date(programadaPara).getTime() : NaN;
  return Number.isFinite(t) && t > ahora.getTime() + 5 * 60_000 ? new Date(t).toISOString() : null;
}

/** Lo que dice «Cuándo» de esa fecha, en la hora de la zona de la campaña (la del selector), como la dice la serie. */
export const textoFechaQueLeToca = (iso: string, zona: string) =>
  `«Programar» viene con la fecha que le toca a esta repetición: el ${fechaDeLaSerie(iso, zona)} (hora de ${paisDeZona(zona)}).`;

/**
 * «La serie ya se probó con esta plantilla el 27/10.»: al lado de «Seguir sin probar», si la última prueba que llegó en la
 * serie (`pruebaDeLaSerie`) es de la plantilla elegida. Es un dato: la prueba de ésta se manda o se saltea a propósito.
 */
export function textoPruebaDeLaSerie(p: CampanaDetalle["pruebaDeLaSerie"], plantillaId: string): string | null {
  if (!p || !plantillaId || p.plantillaId !== plantillaId || !p.at) return null;
  return `La serie ya se probó con esta plantilla el ${diaYMes(p.at)}.`;
}
